import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { GraduationCap } from 'lucide-react';
import { SimpleMarkdownMessage } from '@/components/ui/simple-markdown-message';

interface MessageProps {
  content: string;
  sender: 'user' | 'coach';
  timestamp: Date;
}

export function Message({ content, sender, timestamp }: MessageProps) {
  const isUser = sender === 'user';
  const isMobile = useMediaQuery('(max-width: 640px)');

  const formattedTime = timestamp.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 100, damping: 20 }}
      className={cn(
        'flex mb-3 sm:mb-4',
        isUser ? 'justify-end' : 'justify-start',
      )}
    >
      {/* Enhanced Coach avatar */}
      {!isUser && (
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2 }}
          className="flex-shrink-0 mt-1"
        >
          <div
            className={cn(
              'rounded-full flex items-center justify-center shadow-sm',
              'w-8 h-8 sm:w-10 sm:h-10',
              'bg-primary/10 border-2 border-white',
            )}
          >
            <GraduationCap className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          </div>
        </motion.div>
      )}

      {/* Enhanced Message bubble with modern design */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1 }}
        className={cn(
          'mx-3 px-4 py-3 sm:px-5 sm:py-4 rounded-2xl',
          'max-w-[80%] sm:max-w-[75%] relative',
          isUser
            ? 'bg-primary text-white'
            : 'bg-white text-gray-800 border border-gray-200',
          // Enhanced rounded corners based on sender
          isUser ? 'rounded-tr-md' : 'rounded-tl-md',
        )}
      >
        {/* Message tail */}
        <div
          className={cn(
            'absolute top-3 w-3 h-3 transform rotate-45',
            isUser
              ? 'right-[-6px] bg-primary'
              : 'left-[-6px] bg-white border-l border-t border-gray-200',
          )}
        />
        
        <div className="text-sm sm:text-base leading-relaxed sm:leading-relaxed relative z-10">
          <SimpleMarkdownMessage 
            content={content}
            variant={isUser ? 'user' : 'coach'}
            className="whitespace-pre-wrap"
          />
        </div>
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className={cn(
            'text-[11px] sm:text-xs mt-2 pt-1',
            isUser ? 'text-white/80' : 'text-gray-500',
            'flex items-center justify-between relative z-10',
          )}
        >
          <span className="flex items-center">
            {formattedTime}
            {/* Enhanced read indicator for user messages */}
            {isUser && (
              <motion.svg
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4 }}
                className="h-3 w-3 ml-2 text-white/80"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6L9 17l-5-5" />
              </motion.svg>
            )}
          </span>
          
        </motion.div>
      </motion.div>

      {/* Enhanced User avatar */}
      {isUser && (
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2 }}
          className="flex-shrink-0 mt-1"
        >
          <div
            className={cn(
              'rounded-full flex items-center justify-center shadow-sm',
              'w-8 h-8 sm:w-10 sm:h-10',
              'bg-primary border-2 border-white',
            )}
          >
            <span
              className={cn('text-white font-medium', isMobile ? 'text-sm' : 'text-base')}
            >
              👤
            </span>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
